// src/components/Contact/Contact.js
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Typography, Form, Input, Button, Divider, message } from 'antd';
import { 
  PhoneFilled, 
  MailFilled, 
  EnvironmentFilled,
  ClockCircleFilled,
  SendOutlined,
  MessageFilled,
  UserOutlined,
  PhoneOutlined,
  MailOutlined
} from '@ant-design/icons';
import './Contact.css';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById('contact');
      if (section) {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          setIsVisible(true);
        }
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success('Your message has been sent successfully!');
      form.resetFields();
    } catch (error) {
      message.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactMethods = [
    {
      icon: <PhoneFilled />,
      title: 'Phone',
      details: ['+255 766 764 387', '+255 772 556 030'],
      description: 'Call us during business hours'
    },
    {
      icon: <MailFilled />,
      title: 'Email',
      details: ['info@nexusflow.co.tz', 'support@nexusflow.co.tz'],
      description: 'We respond within 24 hours'
    },
    {
      icon: <EnvironmentFilled />,
      title: 'Office',
      details: ['Tiger Tower, Togo Street, Kinondoni', 'Dar es Salaam, Tanzania'],
      description: 'Visit our headquarters'
    },
    {
      icon: <ClockCircleFilled />,
      title: 'Business Hours',
      details: ['Mon - Fri: 8:00 AM - 6:00 PM', 'Sat: 9:00 AM - 2:00 PM'],
      description: 'Closed on Sundays'
    }
  ];

  return (
    <section id="contact" className="contact-section">
      <div className="container">
        {/* Header */}
        <div className={`section-header ${isVisible ? 'visible' : ''}`}>
          <Title level={1} className="main-title">
            Get In <span className="title-accent">Touch</span>
          </Title>
          <Paragraph className="section-subtitle">
            We're here to help you with all your transportation needs. Reach out to us through 
            any of the following channels and our team will be happy to assist you.
          </Paragraph>
        </div>

        {/* Contact Methods */}
        <Row gutter={[32, 32]} className={`contact-methods ${isVisible ? 'visible' : ''}`}>
          {contactMethods.map((method, index) => (
            <Col xs={24} sm={12} lg={6} key={index}>
              <Card className="contact-method-card" hoverable>
                <div className="method-icon">{method.icon}</div>
                <Title level={4} className="method-title">{method.title}</Title>
                <div className="method-details">
                  {method.details.map((detail, i) => (
                    <Text key={i} className="method-detail">{detail}</Text>
                  ))}
                </div>
                <Text className="method-description">{method.description}</Text>
              </Card>
            </Col>
          ))}
        </Row>

        <Divider className="section-divider" />

        {/* Contact Form & Map */}
        <Row gutter={[48, 32]} className="contact-content">
          {/* Contact Form */}
          <Col xs={24} lg={12}>
            <div className={`form-container ${isVisible ? 'visible' : ''}`}>
              <Title level={2} className="form-title">
                Send Us a <span className="title-underline">Message</span>
              </Title>
              <Paragraph className="form-subtitle">
                Fill out the form below and our team will get back to you as soon as possible.
              </Paragraph>
              
              <Form
                form={form}
                name="contact-form"
                onFinish={onFinish}
                layout="vertical"
                className="contact-form"
              >
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name"
                      rules={[{ required: true, message: 'Please enter your name' }]}
                    >
                      <Input 
                        prefix={<UserOutlined />} 
                        placeholder="Full Name" 
                        size="large"
                        className="form-input"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      rules={[
                        { required: true, message: 'Please enter your email' },
                        { type: 'email', message: 'Please enter a valid email' }
                      ]}
                    >
                      <Input 
                        prefix={<MailOutlined />} 
                        placeholder="Email Address" 
                        size="large"
                        className="form-input"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Form.Item
                  name="phone"
                  rules={[{ required: true, message: 'Please enter your phone number' }]}
                >
                  <Input 
                    prefix={<PhoneOutlined />} 
                    placeholder="Phone Number" 
                    size="large"
                    className="form-input"
                  />
                </Form.Item>
                
                <Form.Item
                  name="subject"
                  rules={[{ required: true, message: 'Please enter a subject' }]}
                >
                  <Input 
                    prefix={<MessageFilled />} 
                    placeholder="Subject" 
                    size="large"
                    className="form-input"
                  />
                </Form.Item>
                
                <Form.Item
                  name="message"
                  rules={[{ required: true, message: 'Please enter your message' }]}
                >
                  <TextArea 
                    placeholder="Your Message" 
                    rows={6}
                    className="form-textarea"
                  />
                </Form.Item>
                
                <Form.Item>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    size="large"
                    loading={loading}
                    icon={<SendOutlined />}
                    className="submit-button"
                  >
                    Send Message
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </Col>

          {/* Map & Additional Info */}
          <Col xs={24} lg={12}>
            <div className={`map-info-container ${isVisible ? 'visible' : ''}`}>
              {/* Embedded Google Map */}
              <Card className="map-card" title="Our Location">
                <div className="map-container">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3962.238366196502!2d39.24381117568758!3d-6.77413466636305!2m3!1f0!2f0!3f0!3m2!1i1020!2i768!4f13.1!3m3!1m2!1s0x185c4f761bb7beb5%3A0x5b48b3a0b4a4b4b4!2sTiger%20Tower%2C%20Togo%20Street%2C%20Dar%20es%20Salaam%2C%20Tanzania!5e0!3m2!1sen!2sus!4v1691234567890!5m2!1sen!2sus"
                    width="100%"
                    height="300"
                    style={{ border: 0, borderRadius: '12px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="NexusFlow Office Location - Tiger Tower, Dar es Salaam"
                    className="google-map"
                  ></iframe>
                  <div className="map-overlay-info">
                    <EnvironmentFilled className="map-pin-icon" />
                    <Text className="map-address">
                      Tiger Tower, Togo Street, Kinondoni, Dar es Salaam, Tanzania
                    </Text>
                  </div>
                </div>
              </Card>

              {/* Additional Contact Info */}
              <Card className="info-card" title="Quick Support">
                <div className="support-info">
                  <div className="support-item">
                    <PhoneFilled className="support-icon" />
                    <div className="support-content">
                      <Text strong>Emergency Support</Text>
                      <Text>+255 766 764 387</Text>
                    </div>
                  </div>
                  <div className="support-item">
                    <MailFilled className="support-icon" />
                    <div className="support-content">
                      <Text strong>Urgent Inquiries</Text>
                      <Text>support@nexusflow.co.tz</Text>
                    </div>
                  </div>
                  <div className="support-item">
                    <ClockCircleFilled className="support-icon" />
                    <div className="support-content">
                      <Text strong>Response Time</Text>
                      <Text>Within 1 hour for urgent matters</Text>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </Col>
        </Row>

        {/* FAQ Section */}
        <div className={`faq-section ${isVisible ? 'visible' : ''}`}>
          <Title level={2} className="faq-title text-center">
            Frequently Asked <span className="title-underline">Questions</span>
          </Title>
          <Row gutter={[32, 32]} className="faq-grid">
            <Col xs={24} md={12}>
              <Card className="faq-card" hoverable>
                <Title level={4} className="faq-question">
                  What are your business hours?
                </Title>
                <Paragraph className="faq-answer">
                  Our customer service team is available Monday to Friday from 8:00 AM to 6:00 PM, 
                  and Saturdays from 9:00 AM to 2:00 PM. We're closed on Sundays and public holidays.
                </Paragraph>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card className="faq-card" hoverable>
                <Title level={4} className="faq-question">
                  How quickly do you respond to inquiries?
                </Title>
                <Paragraph className="faq-answer">
                  We typically respond to all inquiries within 24 hours. For urgent matters, 
                  please call our emergency support line for immediate assistance.
                </Paragraph>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card className="faq-card" hoverable>
                <Title level={4} className="faq-question">
                  Do you provide emergency transportation services?
                </Title>
                <Paragraph className="faq-answer">
                  Yes, we offer 24/7 emergency transportation services for urgent cargo needs. 
                  Contact our emergency line for immediate assistance.
                </Paragraph>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card className="faq-card" hoverable>
                <Title level={4} className="faq-question">
                  Can I visit your office without an appointment?
                </Title>
                <Paragraph className="faq-answer">
                  While walk-ins are welcome, we recommend scheduling an appointment to ensure 
                  that the appropriate team members are available to assist you with your specific needs.
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </section>
  );
};

export default Contact;